import { TableNode } from '@lexical/table';

/**
 * Patches TableNode to support a custom table-wide width (pixels).
 * Follows the same pattern as StyledTableCellNode.jsx — must be imported
 * before the editor is created.
 *
 * __tableWidth values:
 *   null   → no inline width; CSS width:100% from the stylesheet applies
 *   number → constrains the table to exactly that many pixels
 *
 * Lexical's TableNode.createDOM() returns either:
 *   • a <table> element  (when hasHorizontalScroll is off)
 *   • a <div style="overflow-x:auto"> wrapping the <table> (default)
 *
 * In both cases `dom` in our hooks is the element returned by createDOM.
 * We set the width on the outer element AND ensure the inner <table> is
 * 100% so it fills the outer container (preventing overflow / hidden cols).
 */

TableNode.prototype.__tableWidth = null;

// ── Accessors ─────────────────────────────────────────────────────────────────

TableNode.prototype.getTableWidth = function () {
  return this.__tableWidth ?? null;
};

TableNode.prototype.setTableWidth = function (width) {
  const w = this.getWritable();
  w.__tableWidth = width ?? null;
  return w;
};

// ── DOM helper ────────────────────────────────────────────────────────────────

function applyWidthToDOM(tableWidth, dom) {
  // `dom` may be the wrapper <div> or the <table> itself.
  // In both cases set the width on dom so the container is constrained.
  if (tableWidth == null) {
    dom.style.removeProperty('width');
  } else {
    dom.style.width = `${tableWidth}px`;
    // If dom is the scrollable wrapper, make the inner <table> fill it
    // so columns scale within the box rather than overflowing.
    const inner = dom.tagName === 'TABLE' ? null : dom.querySelector('table');
    if (inner) {
      inner.style.width = '100%';
    }
  }
}

// ── createDOM ─────────────────────────────────────────────────────────────────

const _origCreateDOM = TableNode.prototype.createDOM;
TableNode.prototype.createDOM = function (config, editor) {
  const dom = _origCreateDOM.call(this, config, editor);
  if (this.__tableWidth != null) applyWidthToDOM(this.__tableWidth, dom);
  return dom;
};

// ── updateDOM ─────────────────────────────────────────────────────────────────

const _origUpdateDOM = TableNode.prototype.updateDOM;
TableNode.prototype.updateDOM = function (prevNode, dom, config) {
  const result = _origUpdateDOM.call(this, prevNode, dom, config);
  if (prevNode.__tableWidth !== this.__tableWidth) {
    applyWidthToDOM(this.__tableWidth, dom);
  }
  return result;
};

// ── exportJSON ────────────────────────────────────────────────────────────────

const _origExportJSON = TableNode.prototype.exportJSON;
TableNode.prototype.exportJSON = function () {
  const json = _origExportJSON.call(this);
  if (this.__tableWidth != null) json.tableWidth = this.__tableWidth;
  return json;
};

// ── importJSON (static) ───────────────────────────────────────────────────────

const _origImportJSON = TableNode.importJSON;
TableNode.importJSON = function (serializedNode) {
  const node = _origImportJSON.call(this, serializedNode);
  if (serializedNode.tableWidth != null) node.__tableWidth = serializedNode.tableWidth;
  return node;
};

// ── clone (static) ────────────────────────────────────────────────────────────

const _origClone = TableNode.clone;
TableNode.clone = function (node) {
  const cloned = _origClone.call(this, node);
  cloned.__tableWidth = node.__tableWidth ?? null;
  return cloned;
};

// ── exportDOM — applies width with !important for PDF / print HTML ─────────────

const _origExportDOM = TableNode.prototype.exportDOM;
TableNode.prototype.exportDOM = function (editor) {
  const result = _origExportDOM.call(this, editor);
  if (result.element && this.__tableWidth != null) {
    const el = result.element;
    // exportDOM always returns the <table> element (not the wrapper)
    el.style.setProperty('width', `${this.__tableWidth}px`, 'important');
  }
  return result;
};
